import { api, Badge, Banner, dateTime, Empty, PageHead, stackOnMobile, useAsync } from "@getaways/extranet-ui";
import { useState } from "react";
import { useCan } from "../lib/access";
import { cadenceText, PERIODS, type Cadence } from "../lib/schedule";

interface Schedule {
  id: string;
  title: string;
  ownerEmail: string;
  period: string;
  format: string;
  cadence: Cadence;
  recipients: string[];
  chatWebhook: string | null;
  active: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  lastStatus: string | null;
}

const TONE: Record<string, "ok" | "bad" | "warn" | "neutral"> = { sent: "ok", logged: "neutral", failed: "bad", stopped: "warn" };

/** Scheduled sends (docs/36 §9). Any table's "Schedule" button adds one; here they are sent at once, paused or removed. */
export function SchedulesPage() {
  const admin = useCan()("sync:manage");
  const [everyone, setEveryone] = useState(false);
  const { data, error, reload } = useAsync(() => api<{ schedules: Schedule[] }>("GET", `/v1/schedules${everyone ? "?all=true" : ""}`), [everyone]);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string }>();
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      setNotice({ tone: "ok", text: ok });
    } catch (err) {
      setNotice({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
    }
    reload();
  };
  const send = (s: Schedule) => act(async () => {
    const r = await api<{ status: string; detail: string | null }>("POST", `/v1/schedules/${s.id}/send`);
    if (r.status === "failed" || r.status === "stopped") throw new Error(r.detail ?? r.status);
    return r;
  }, `“${s.title}” sent.`);
  const toggle = (s: Schedule) => act(() => api("PUT", `/v1/schedules/${s.id}`, { title: s.title, spec: (s as unknown as { spec: unknown }).spec, period: s.period, format: s.format, cadence: s.cadence, recipients: s.recipients, chatWebhook: s.chatWebhook, active: !s.active }), s.active ? "Paused." : "Resumed.");
  const remove = (s: Schedule) => act(() => api("DELETE", `/v1/schedules/${s.id}`), "Removed.");

  return (
    <>
      <PageHead title="Schedules" sub="Reports sent on their own, by email or to Google Chat, with the access of whoever made them.">
        {admin && <label className="row small" style={{ gap: 6 }}><input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} /> Everyone's</label>}
      </PageHead>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {notice && <Banner notice={notice} onClose={() => setNotice(undefined)} />}
      <div className="card" style={{ padding: 0 }}>
        {data && data.schedules.length === 0 ? <Empty>No schedule yet. Use “Schedule” on any table of a dashboard.</Empty> : (
          <table ref={stackOnMobile} className="table stack-mobile">
            <thead><tr><th>Report</th><th>When</th><th>Covers</th><th>To</th><th>Next</th><th>Last</th><th /></tr></thead>
            <tbody>
              {(data?.schedules ?? []).map((s) => (
                <tr key={s.id}>
                  <td>{s.title}{everyone && <div className="small muted">{s.ownerEmail}</div>}{!s.active && <Badge tone="warn">paused</Badge>}</td>
                  <td className="small">{cadenceText(s.cadence)}</td>
                  <td className="small">{PERIODS[s.period] ?? s.period} · {s.format === "csv" ? "CSV" : "Excel"}</td>
                  <td className="small">{[...s.recipients, ...(s.chatWebhook ? ["Google Chat"] : [])].join(", ")}</td>
                  <td className="small">{s.active ? dateTime(s.nextRunAt, "Europe/Lisbon") : "—"}</td>
                  <td className="small">{s.lastStatus ? <><Badge tone={TONE[s.lastStatus] ?? "neutral"}>{s.lastStatus}</Badge> {dateTime(s.lastRunAt ?? undefined, "Europe/Lisbon")}</> : "—"}</td>
                  <td className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    <button className="ghost" onClick={() => void send(s)}>Send now</button>
                    <button className="ghost" onClick={() => void toggle(s)}>{s.active ? "Pause" : "Resume"}</button>
                    <button className="ghost" onClick={() => void remove(s)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="small muted">“logged” means sending is switched off on this server: the report was built but no email or Chat message left.</p>
    </>
  );
}
